import { defineComponent } from 'vue';
import { Command } from '@/store/types';
import { bindShortcuts } from '@shortcuts/index';
import router from '@/vue/router';

/**
 * Command mixin, expects commands option.
 *
 * The commands are added to the globalSearchModal via its store
 *
 * @example
 * mixins: [commandMixin],
 *
 * commands() {
 *   return [{
 *     id: 'SourceDetail',
 *     group: 'document',
 *     translationKey: 'action.open',
 *     callback: this.open,
 *     icon: 'OpenIcon16',
 *    }];
 * },
 *
 */

export const commandMixin = defineComponent({
  data(): {
    commandsBound: boolean;
    _unbindShortcuts?: () => void;
  } {
    return {
      commandsBound: false,
    };
  },

  methods: {
    $bindCommands() {
      if (this.commandsBound) return;

      this.commandsBound = true;

      const commands = <Array<Command>>this.$options.commands.call(this);

      this.$store.commit(
        'globalSearch/addCommands',
        commands.map((command) => ({ ...command, id: this.$.vnode.component.type.name }))
      );

      // Add keyboard shortcut if command has one
      // Wait a tick so that another component that gets destroyed at the same time deregisters
      // its listener before the new one is added
      this.$nextTick(() => {
        this._unbindShortcuts = bindShortcuts(
          commands
            .filter((command) => Boolean(command.shortcut))
            .reduce(
              (previous, command) => ({
                ...previous,
                [command.shortcut]:
                  command.callback ||
                  (() => {
                    router.push({
                      name: command.route.name,
                    });
                  }),
              }),
              {}
            )
        );
      });
    },

    $unbindCommands() {
      if (!this.commandsBound) return;

      this._unbindShortcuts();

      const commands = <Array<Command>>this.$options.commands.call(this);

      this.$store.commit(
        'globalSearch/removeCommands',
        commands.map((command) => ({ ...command, id: this.$.vnode.component.type.name }))
      );
      this.commandsBound = false;
    },
  },

  mounted() {
    this.$bindCommands();
  },

  activated() {
    this.$bindCommands();
  },

  beforeUnmount() {
    this.$unbindCommands();
  },

  deactivated() {
    this.$unbindCommands();
  },
});

export default commandMixin;
